/**
 * E-Commerce Analytics Page
 *
 * Comprehensive analytics dashboard with two distinct sections:
 * 1. Sales Analytics - For physical, digital, and service products
 * 2. Affiliate Analytics - For affiliate products (traffic-focused)
 */

import { useState, useEffect, useCallback } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { api } from '../../lib/api';

interface Store {
  id: string;
  name: string;
  currency: string;
}

interface SalesAnalytics {
  overview: {
    revenue: number;
    revenueChange: number;
    orders: number;
    ordersChange: number;
    averageOrderValue: number;
    aovChange: number;
    itemsSold: number;
    itemsSoldChange: number;
    customers: number;
    customersChange: number;
  };
  revenueByDay: Array<{ date: string; revenue: number; orders: number }>;
  topProducts: Array<{
    id: string;
    name: string;
    image: string | null;
    productType: string;
    revenue: number;
    quantity: number;
    orders: number;
  }>;
  topCategories: Array<{
    id: string;
    name: string;
    revenue: number;
    orders: number;
    products: number;
  }>;
  ordersByStatus: Record<string, number>;
  paymentStatus: Record<string, number>;
  recentOrders: Array<{
    id: string;
    orderNumber: string;
    customerName: string;
    total: number;
    status: string;
    createdAt: string;
  }>;
  lowStockProducts: Array<{
    id: string;
    name: string;
    image: string | null;
    inventory: number;
    threshold: number;
  }>;
  customerInsights: {
    newCustomers: number;
    returningCustomers: number;
    avgOrdersPerCustomer: number;
  };
  productTypeSummary: {
    physical: { count: number; revenue: number };
    digital: { count: number; revenue: number };
    service: { count: number; revenue: number };
  };
}

interface AffiliateAnalytics {
  overview: {
    totalProducts: number;
    activeProducts: number;
    totalClicks: number;
    clicksChange: number;
    avgClicksPerProduct: number;
    brokenLinkReports: number;
    pendingReports: number;
  };
  clicksByDay: Array<{ date: string; clicks: number }>;
  topProducts: Array<{
    id: string;
    name: string;
    image: string | null;
    clicks: number;
    source: string;
    commissionRate: number | null;
    status: string;
  }>;
  bySource: Array<{ source: string; products: number; clicks: number }>;
  byCategory: Array<{ id: string; name: string; products: number; clicks: number }>;
  recentlyAdded: Array<{
    id: string;
    name: string;
    image: string | null;
    source: string;
    clicks: number;
    createdAt: string;
  }>;
  brokenLinks: Array<{
    id: string;
    productId: string;
    productName: string;
    reportedUrl: string | null;
    status: string;
    createdAt: string;
  }>;
}

type Period = '7d' | '30d' | '90d' | '365d' | 'all';
type Tab = 'sales' | 'affiliate';

const periodLabels: Record<Period, string> = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  '365d': 'Last year',
  'all': 'All time',
};

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  confirmed: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  processing: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  shipped: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
  delivered: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  refunded: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  reviewed: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  fixed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  ignored: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  invalid: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

const sourceLabels: Record<string, string> = {
  aliexpress: 'AliExpress',
  amazon: 'Amazon',
  ebay: 'eBay',
  shopee: 'Shopee',
  cj: 'CJ Affiliate',
  shareasale: 'ShareASale',
  rakuten: 'Rakuten',
  awin: 'Awin',
  impact: 'Impact',
  unknown: 'Other',
};

export default function AnalyticsPage() {
  const { store } = useOutletContext<{ store: Store }>();
  const [sales, setSales] = useState<SalesAnalytics | null>(null);
  const [affiliate, setAffiliate] = useState<AffiliateAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<Period>('30d');
  const [activeTab, setActiveTab] = useState<Tab>('sales');
  const [currency, setCurrency] = useState('USD');

  const loadAnalytics = useCallback(async () => {
    if (!store?.id) return;

    try {
      setLoading(true);
      setError(null);

      const response = await api.get(`/api/ecommerce/stores/${store.id}/analytics?period=${period}`);
      if (!response.ok) {
        throw new Error('Failed to load analytics');
      }

      const data = await response.json() as {
        sales: SalesAnalytics;
        affiliate: AffiliateAnalytics;
        currency: string;
      };
      setSales(data.sales);
      setAffiliate(data.affiliate);
      setCurrency(data.currency || 'USD');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [store?.id, period]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount / 100);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const ChangeIndicator = ({ value, suffix = '%' }: { value: number; suffix?: string }) => {
    if (value === 0) return <span className="text-gray-500 text-sm">No change</span>;
    const isPositive = value > 0;
    return (
      <span className={`flex items-center gap-1 text-sm ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
        <svg className={`w-4 h-4 ${isPositive ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
        {Math.abs(value)}{suffix}
      </span>
    );
  };

  // Chart components
  const BarChart = ({ data, valueKey, labelKey, formatValue }: {
    data: Array<Record<string, unknown>>;
    valueKey: string;
    labelKey: string;
    formatValue: (v: number) => string;
  }) => {
    if (data.length === 0) {
      return <div className="flex items-center justify-center h-48 text-gray-500">No data available</div>;
    }

    const maxValue = Math.max(...data.map(d => d[valueKey] as number), 1);
    const chartHeight = 200;

    return (
      <div className="relative">
        <div className="absolute left-0 top-0 bottom-8 w-16 flex flex-col justify-between text-xs text-gray-500">
          <span>{formatValue(maxValue)}</span>
          <span>{formatValue(maxValue / 2)}</span>
          <span>{formatValue(0)}</span>
        </div>
        <div className="ml-16">
          <div className="flex items-end gap-1" style={{ height: chartHeight }}>
            {data.map((item, index) => {
              const height = maxValue > 0 ? ((item[valueKey] as number) / maxValue) * chartHeight : 0;
              return (
                <div key={index} className="flex-1 group relative">
                  <div
                    className="bg-teal-500 hover:bg-teal-600 rounded-t transition-all cursor-pointer"
                    style={{ height: Math.max(height, 2) }}
                  />
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                    <div className="bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                      <div className="font-medium">{formatDate(item[labelKey] as string)}</div>
                      <div>{formatValue(item[valueKey] as number)}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-2 text-xs text-gray-500">
            {data.length > 0 && (
              <>
                <span>{formatDate(data[0][labelKey] as string)}</span>
                {data.length > 7 && <span>{formatDate(data[Math.floor(data.length / 2)][labelKey] as string)}</span>}
                <span>{formatDate(data[data.length - 1][labelKey] as string)}</span>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-48" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded-xl" />
            ))}
          </div>
          <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded-xl" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
          <p className="text-red-600 dark:text-red-400">{error}</p>
          <button onClick={loadAnalytics} className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!sales || !affiliate) return null;

  const totalOrders = Object.values(sales.ordersByStatus).reduce((a, b) => a + b, 0);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Analytics</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Track your store performance and insights</p>
        </div>
        <div className="flex items-center gap-2">
          {(Object.keys(periodLabels) as Period[]).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                period === p
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {periodLabels[p]}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-8">
          <button
            onClick={() => setActiveTab('sales')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'sales'
                ? 'border-teal-500 text-teal-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Sales Analytics
            </div>
          </button>
          <button
            onClick={() => setActiveTab('affiliate')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'affiliate'
                ? 'border-teal-500 text-teal-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              Affiliate Analytics
              {affiliate.overview.pendingReports > 0 && (
                <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full text-xs font-medium">
                  {affiliate.overview.pendingReports}
                </span>
              )}
            </div>
          </button>
        </nav>
      </div>

      {/* Sales Analytics Tab */}
      {activeTab === 'sales' && (
        <div className="space-y-6">
          {/* Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">Revenue</span>
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{formatCurrency(sales.overview.revenue)}</div>
              <ChangeIndicator value={sales.overview.revenueChange} />
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">Orders</span>
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{formatNumber(sales.overview.orders)}</div>
              <ChangeIndicator value={sales.overview.ordersChange} />
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">Avg Order Value</span>
                <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{formatCurrency(sales.overview.averageOrderValue)}</div>
              <ChangeIndicator value={sales.overview.aovChange} />
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">Items Sold</span>
                <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{formatNumber(sales.overview.itemsSold)}</div>
              <ChangeIndicator value={sales.overview.itemsSoldChange} />
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">Customers</span>
                <div className="w-10 h-10 bg-teal-100 dark:bg-teal-900/30 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{formatNumber(sales.overview.customers)}</div>
              <ChangeIndicator value={sales.overview.customersChange} />
            </div>
          </div>

          {/* Revenue Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Revenue Over Time</h2>
            <BarChart data={sales.revenueByDay} valueKey="revenue" labelKey="date" formatValue={formatCurrency} />
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Products */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Top Products</h2>
                <Link to={`/store/${store.id}/products`} className="text-sm text-teal-600 hover:text-teal-700">View all</Link>
              </div>
              {sales.topProducts.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No product data yet</p>
              ) : (
                <div className="space-y-3">
                  {sales.topProducts.slice(0, 5).map((product) => (
                    <Link
                      key={product.id}
                      to={`/store/${store.id}/products/${product.id}`}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden flex-shrink-0">
                        {product.image ? (
                          <img src={product.image} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white truncate">{product.name}</p>
                        <p className="text-sm text-gray-500">{product.quantity} sold</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900 dark:text-white">{formatCurrency(product.revenue)}</p>
                        <p className="text-sm text-gray-500">{product.orders} orders</p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Recent Orders */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Orders</h2>
                <Link to={`/store/${store.id}/orders`} className="text-sm text-teal-600 hover:text-teal-700">View all</Link>
              </div>
              {sales.recentOrders.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No orders yet</p>
              ) : (
                <div className="space-y-3">
                  {sales.recentOrders.slice(0, 5).map((order) => (
                    <Link
                      key={order.id}
                      to={`/store/${store.id}/orders/${order.id}`}
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">#{order.orderNumber}</p>
                        <p className="text-sm text-gray-500">{order.customerName}</p>
                        <p className="text-xs text-gray-400">{formatDateTime(order.createdAt)}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900 dark:text-white">{formatCurrency(order.total)}</p>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusColors[order.status] || 'bg-gray-100 text-gray-800'}`}>
                          {order.status}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Three Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Order Status */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Orders by Status</h2>
              {totalOrders === 0 ? (
                <p className="text-gray-500 text-center py-8">No orders yet</p>
              ) : (
                <div className="space-y-3">
                  {Object.entries(sales.ordersByStatus).map(([status, count]) => (
                    <div key={status} className="flex items-center justify-between">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
                        {status}
                      </span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{count} ({Math.round((count / totalOrders) * 100)}%)</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Product Types */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Product Types</h2>
              <div className="space-y-3">
                {Object.entries(sales.productTypeSummary).map(([type, data]) => (
                  <div key={type} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-gray-900 dark:text-white capitalize">{type}</span>
                      <span className="text-sm text-gray-500">{data.count} products</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Revenue</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(data.revenue)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Low Stock Alert */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Low Stock Alert</h2>
                {sales.lowStockProducts.length > 0 && (
                  <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full text-xs font-medium">
                    {sales.lowStockProducts.length}
                  </span>
                )}
              </div>
              {sales.lowStockProducts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-3">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-gray-500">All products are well stocked!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {sales.lowStockProducts.slice(0, 5).map((product) => (
                    <Link
                      key={product.id}
                      to={`/store/${store.id}/products/${product.id}`}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white truncate">{product.name}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-sm font-medium ${
                        product.inventory === 0
                          ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                          : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
                      }`}>
                        {product.inventory} left
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Affiliate Analytics Tab */}
      {activeTab === 'affiliate' && (
        <div className="space-y-6">
          {/* Info Banner */}
          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-purple-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm text-purple-800 dark:text-purple-200">
                  <strong>Affiliate products are traffic-focused.</strong> Revenue cannot be calculated directly as commissions are typically paid months after referrals and depend on actual purchases made on external platforms.
                </p>
              </div>
            </div>
          </div>

          {/* Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">Total Products</span>
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{formatNumber(affiliate.overview.totalProducts)}</div>
              <span className="text-sm text-gray-500">{affiliate.overview.activeProducts} active</span>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">Total Clicks</span>
                <div className="w-10 h-10 bg-teal-100 dark:bg-teal-900/30 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                  </svg>
                </div>
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{formatNumber(affiliate.overview.totalClicks)}</div>
              <ChangeIndicator value={affiliate.overview.clicksChange} />
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">Avg Clicks/Product</span>
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{formatNumber(affiliate.overview.avgClicksPerProduct)}</div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">Sources</span>
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{affiliate.bySource.length}</div>
              <span className="text-sm text-gray-500">platforms</span>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">Categories</span>
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{affiliate.byCategory.length}</div>
              <span className="text-sm text-gray-500">with affiliates</span>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">Link Reports</span>
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{formatNumber(affiliate.overview.brokenLinkReports)}</div>
              <span className="text-sm text-gray-500">total reported</span>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700 border-l-4 border-l-red-500">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">Pending Reports</span>
                <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{formatNumber(affiliate.overview.pendingReports)}</div>
              <Link to={`/store/${store.id}/broken-links`} className="text-sm text-red-600 hover:text-red-700">Review now</Link>
            </div>
          </div>

          {/* Clicks Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Clicks Over Time</h2>
            <BarChart data={affiliate.clicksByDay} valueKey="clicks" labelKey="date" formatValue={(v) => formatNumber(v)} />
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Affiliate Products */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Top Affiliate Products</h2>
                <Link to={`/store/${store.id}/products`} className="text-sm text-teal-600 hover:text-teal-700">View all</Link>
              </div>
              {affiliate.topProducts.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No affiliate products yet</p>
              ) : (
                <div className="space-y-3">
                  {affiliate.topProducts.slice(0, 5).map((product) => (
                    <Link
                      key={product.id}
                      to={`/store/${store.id}/products/${product.id}`}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden flex-shrink-0">
                        {product.image ? (
                          <img src={product.image} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white truncate">{product.name}</p>
                        <p className="text-sm text-gray-500">{sourceLabels[product.source] || product.source}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-teal-600">{formatNumber(product.clicks)} clicks</p>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusColors[product.status] || 'bg-gray-100 text-gray-800'}`}>
                          {product.status}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* By Source */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">By Source Platform</h2>
              {affiliate.bySource.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No data yet</p>
              ) : (
                <div className="space-y-3">
                  {affiliate.bySource.map((source) => (
                    <div key={source.source} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900 dark:text-white">{sourceLabels[source.source] || source.source}</span>
                        <span className="text-sm text-gray-500">{source.products} products</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-2 mr-3">
                          <div
                            className="bg-teal-500 h-full rounded-full"
                            style={{ width: `${affiliate.overview.totalClicks > 0 ? (source.clicks / affiliate.overview.totalClicks) * 100 : 0}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{formatNumber(source.clicks)} clicks</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* By Category and Broken Links */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* By Category */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">By Category</h2>
              {affiliate.byCategory.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No categories with affiliate products</p>
              ) : (
                <div className="space-y-3">
                  {affiliate.byCategory.slice(0, 5).map((category) => (
                    <div key={category.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{category.name}</p>
                        <p className="text-sm text-gray-500">{category.products} products</p>
                      </div>
                      <span className="text-teal-600 font-semibold">{formatNumber(category.clicks)} clicks</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recent Broken Link Reports */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Broken Link Reports</h2>
                <Link to={`/store/${store.id}/broken-links`} className="text-sm text-teal-600 hover:text-teal-700">View all</Link>
              </div>
              {affiliate.brokenLinks.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-3">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-gray-500">No broken links reported</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {affiliate.brokenLinks.map((report) => (
                    <Link
                      key={report.id}
                      to={`/store/${store.id}/broken-links`}
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white truncate">{report.productName}</p>
                        <p className="text-xs text-gray-400">{formatDateTime(report.createdAt)}</p>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusColors[report.status] || 'bg-gray-100 text-gray-800'}`}>
                        {report.status}
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Recently Added */}
          {affiliate.recentlyAdded.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recently Added Affiliate Products</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                {affiliate.recentlyAdded.map((product) => (
                  <Link
                    key={product.id}
                    to={`/store/${store.id}/products/${product.id}`}
                    className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div className="w-full h-24 bg-gray-200 dark:bg-gray-600 rounded-lg overflow-hidden mb-3">
                      {product.image ? (
                        <img src={product.image} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <p className="font-medium text-gray-900 dark:text-white truncate mb-1">{product.name}</p>
                    <p className="text-sm text-gray-500 mb-2">{sourceLabels[product.source] || product.source}</p>
                    <div className="flex items-center gap-1 text-teal-600">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                      </svg>
                      <span className="font-semibold">{formatNumber(product.clicks)}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
